import { useState, type ReactNode } from 'react';
import { useI18n } from '@/i18n/i18n-provider';
import { Switch } from '@/components/ui';
import {
  getShowArchivedRecordsPreference,
  getShowDeletedRecordsPreference,
  setShowArchivedRecordsPreference,
  setShowDeletedRecordsPreference,
} from '@/services/record-visibility-preferences';

/**
 * Per-browser preferences, not tenant settings — no permission gate beyond
 * a valid session, the same as MFA management. Persisted to `localStorage`
 * rather than the account: nothing here changes what other people at the
 * same church see, only what this browser shows this person. Every list
 * page across the app reads these same two preferences (via
 * `record-visibility-preferences.ts`) to decide its own "show archived"
 * default.
 */
export function MySettingsPage(): ReactNode {
  const { t } = useI18n();
  const [showDeleted, setShowDeleted] = useState(getShowDeletedRecordsPreference);
  const [showArchived, setShowArchived] = useState(getShowArchivedRecordsPreference);

  function toggleShowDeleted(checked: boolean): void {
    setShowDeleted(checked);
    setShowDeletedRecordsPreference(checked);
  }

  function toggleShowArchived(checked: boolean): void {
    setShowArchived(checked);
    setShowArchivedRecordsPreference(checked);
  }

  return (
    <div className="page">
      <header className="page-header">
        <h1 className="page-title">{t('settings.mySettings.title')}</h1>
      </header>

      <p className="field-hint">{t('settings.mySettings.description')}</p>

      <div className="settings-card">
        <Switch
          checked={showDeleted}
          onChange={toggleShowDeleted}
          label={t('settings.mySettings.showDeletedRecords')}
        />
        <Switch
          checked={showArchived}
          onChange={toggleShowArchived}
          label={t('settings.mySettings.showArchivedRecords')}
        />
      </div>
    </div>
  );
}
